import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useRoute, useLocation } from 'wouter';
import { useAuth } from '../hooks/use-auth';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { apiRequest, getQueryFn, queryClient } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { TeamMembersTab } from '../components/team-members-tab';
import type { Country } from '../types';

interface Company {
  id: number;
  name: string;
  industry?: string;
  industry_name?: string;
  size?: string;
  country_code?: string;
  country_name?: string;
  base_year?: number;
  registration_year?: number;
  description?: string;
  owner_id: number;
  created_at: string;
  assigned_countries?: Country[];
}

const industryOptions = [
  { value: '', label: 'Select Industry' },
  { value: 'Manufacturing Industry', label: 'Manufacturing Industry' },
  { value: 'Healthcare Industry', label: 'Healthcare Industry' },
  { value: 'Retail Industry', label: 'Retail Industry' },
  { value: 'Hospitality Industry (Hotels & Restaurants)', label: 'Hospitality Industry (Hotels & Restaurants)' },
  { value: 'IT & Tech Industry', label: 'IT & Tech Industry' },
  { value: 'Construction Industry', label: 'Construction Industry' },
  { value: 'Finance & Corporate Offices', label: 'Finance & Corporate Offices' },
  { value: 'Education Institutions', label: 'Education Institutions' },
  { value: 'Transport & Logistics', label: 'Transport & Logistics' },
  { value: 'Food & Beverage Industry', label: 'Food & Beverage Industry' },
];

const sizeOptions = [
  { value: '', label: 'Select Size' },
  { value: 'small', label: 'Small (1-50 employees)' },
  { value: 'medium', label: 'Medium (51-200 employees)' },
  { value: 'large', label: 'Large (201-1000 employees)' },
  { value: 'enterprise', label: 'Enterprise (1000+ employees)' },
];

export default function CompanyProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [match, params] = useRoute('/company-profile/:id');
  const [, setLocation] = useLocation();
  const id = params?.id;

  const [isEditing, setIsEditing] = useState(!id);
  const [activeTab, setActiveTab] = useState<'details' | 'members'>('details');
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
    country_code: '',
    assigned_countries: [] as string[],
    base_year: 0, // Initialize with 0 to avoid default year conflicts
    registration_year: 0, // Initialize with 0 to avoid default year conflicts
    description: '',
  });

  // Initialize form data with empty values to avoid default year conflicts
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  const { data: currentCompany, isLoading, error } = useQuery<Company>({
    queryKey: [`/api/companies/${id}`],
    queryFn: getQueryFn(),
    enabled: !!id,
  });


  // Fetch user's companies for access control
  const { data: companies } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
  });

  // Fetch all available countries
  const { data: allCountries } = useQuery<Country[]>({
    queryKey: ['/api/countries/'],
    queryFn: getQueryFn(),
  });

  // Update form data when company data is loaded
  useEffect(() => {
    if (currentCompany && !isFormInitialized) {
      const newFormData = {
        name: currentCompany.name || '',
        industry: currentCompany.industry_name || currentCompany.industry || '',
        size: currentCompany.size || '',
        country_code: currentCompany.country_code || '',
        assigned_countries: currentCompany.assigned_countries?.map(c => c.code) || [],
        base_year: currentCompany.base_year || new Date().getFullYear(),
        registration_year: currentCompany.registration_year || new Date().getFullYear(),
        description: currentCompany.description || '',
      };
      
      setFormData(newFormData);
      setIsFormInitialized(true);
    } else if (!currentCompany && !isFormInitialized && !id) {
      // For new companies, set default values
      const newFormData = {
        name: '',
        industry: '',
        size: '',
        country_code: '',
        assigned_countries: [] as string[],
        base_year: new Date().getFullYear(),
        registration_year: new Date().getFullYear(),
        description: '',
      };
      
      setFormData(newFormData);
      setIsFormInitialized(true);
    }
  }, [currentCompany, isFormInitialized, id]);

  // Note: Region change effect has been removed

  const createCompanyMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', '/api/companies', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/companies'] });
      toast({
        title: 'Company Created',
        description: 'Your company profile has been created successfully.',
      });
      setLocation('/companies');
    },
    onError: (error: Error) => {
      toast({
        title: 'Creation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('PUT', `/api/companies/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${id}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/companies'] });
      toast({
        title: 'Profile Updated',
        description: 'Your company profile has been updated successfully.',
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Update Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (id) {
      updateCompanyMutation.mutate(formData);
    } else {
      createCompanyMutation.mutate(formData);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Company name must be at least 2 characters long';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Company name must be less than 255 characters';
    }
    
    if (!formData.base_year) {
      newErrors.base_year = 'Base year is required';
    }
    
    if (!formData.registration_year) {
      newErrors.registration_year = 'Registration year is required';
    }
    
    if (formData.industry && formData.industry.length > 100) {
      newErrors.industry = 'Industry must be less than 100 characters';
    }
    
    // Note: Region validation has been removed

    if (formData.country_code && formData.country_code.length > 10) {
      newErrors.country_code = 'Country code must be less than 10 characters';
    }
    
    if (formData.base_year) {
      const currentYear = new Date().getFullYear();
      if (formData.base_year < 2000 || formData.base_year > currentYear) {
        newErrors.base_year = `Base year must be between 2000 and ${currentYear}`;
      }
    }
    
    if (formData.registration_year) {
      const currentYear = new Date().getFullYear();
      if (formData.registration_year < 2000 || formData.registration_year > currentYear + 1) {
        newErrors.registration_year = `Registration year must be between 2000 and ${currentYear + 1}`;
      }
    }
    
    // Validate year comparison
    if (formData.base_year && formData.registration_year) {
      if (formData.registration_year < formData.base_year) {
        newErrors.registration_year = `Registration year must be greater than or equal to base year (${formData.base_year})`;
      }
    }
    
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 p-8">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Company Profile</h1>
              <p className="text-gray-600 mt-2">
                Manage your organization's information and settings
              </p>
            </div>

            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center">
                    <i data-feather="building" className="w-5 h-5 mr-2"></i>
                    {id ? 'Company Information' : 'Create Company Profile'}
                  </CardTitle>
                  {id && user?.is_system_user && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (isEditing) {
                          // Cancel editing - reset form to original data
                          if (currentCompany) {
                            setFormData({
                              name: currentCompany.name || '',
                              industry: currentCompany.industry_name || currentCompany.industry || '',
                              size: currentCompany.size || '',
                              country_code: currentCompany.country_code || '',
                              assigned_countries: currentCompany.assigned_countries?.map(c => c.code) || [],
                              base_year: currentCompany.base_year || new Date().getFullYear(),
                              registration_year: currentCompany.registration_year || new Date().getFullYear(),
                              description: currentCompany.description || '',
                            });
                          }
                        }
                        setIsEditing(!isEditing);
                      }}
                    >
                      <i data-feather="edit" className="w-4 h-4 mr-2"></i>
                      {isEditing ? 'Cancel' : 'Edit'}
                    </Button>
                  )}
                </div>
                
                {/* Tabs - Only show for existing companies */}
                {id && (
                  <div className="flex space-x-1 mt-4">
                    <button
                      onClick={() => setActiveTab('details')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'details'
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i data-feather="info" className="w-4 h-4 mr-2 inline"></i>
                      Company Details
                    </button>
                    <button
                      onClick={() => setActiveTab('members')}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeTab === 'members'
                          ? 'bg-blue-100 text-blue-700 border border-blue-200'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i data-feather="users" className="w-4 h-4 mr-2 inline"></i>
                      Team Members
                    </button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {activeTab === 'details' && (
                  <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                    <Input
                      label="Company Name *"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter company name"
                      disabled={!isEditing}
                      required
                        className={errors.name ? 'border-red-500' : ''}
                    />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                    <Select
                      label="Industry"
                      options={industryOptions}
                      value={formData.industry}
                      onChange={(e) => handleChange('industry', e.target.value)}
                      disabled={!isEditing}
                      className={errors.industry ? 'border-red-500' : ''}
                    />
                    {errors.industry && (
                      <p className="text-red-500 text-sm mt-1">{errors.industry}</p>
                    )}
                    </div>

                    <Select
                      label="Company Size"
                      options={sizeOptions}
                      value={formData.size}
                      onChange={(e) => handleChange('size', e.target.value)}
                      disabled={!isEditing}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Country
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={currentCompany?.name || formData.name || 'Company Name'}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                          placeholder="Company Name"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <i data-feather="building" className="w-4 h-4 text-gray-400"></i>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Primary country is determined by company location
                      </p>
                    </div>

                    <div>
                    <Input
                      label="Base Year *"
                      type="number"
                      min="2000"
                      max={new Date().getFullYear()}
                      value={formData.base_year}
                      onChange={(e) => handleChange('base_year', parseInt(e.target.value))}
                      disabled={!isEditing}
                      required
                        className={errors.base_year ? 'border-red-500' : ''}
                    />
                      {errors.base_year && (
                        <p className="text-red-500 text-sm mt-1">{errors.base_year}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Base year for environmental impact tracking and comparison. This will be inherited across the platform.
                      </p>
                    </div>

                    <div>
                    <Input
                      label="Registration Year *"
                      type="number"
                      min="2000"
                      max={new Date().getFullYear() + 1}
                      value={formData.registration_year}
                      onChange={(e) => handleChange('registration_year', parseInt(e.target.value))}
                      disabled={!isEditing}
                      required
                        className={errors.registration_year ? 'border-red-500' : ''}
                    />
                      {errors.registration_year && (
                        <p className="text-red-500 text-sm mt-1">{errors.registration_year}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Year when company started tracking emissions. Used for company registration context.
                      </p>
                    </div>
                  </div>

                  {/* Operating Countries Multi-select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operating Countries *
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Select all countries where your company operates. These will be available in emission entry forms.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto border rounded-lg p-4">
                      {(allCountries || []).map((country) => (
                        <label key={country.code} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.assigned_countries.includes(country.code)}
                            onChange={(e) => {
                              const newCountries = e.target.checked
                                ? [...formData.assigned_countries, country.code]
                                : formData.assigned_countries.filter(c => c !== country.code);
                              handleChange('assigned_countries', newCountries);
                            }}
                            disabled={!isEditing}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{country.name}</span>
                        </label>
                      ))}
                    </div>
                    {errors.assigned_countries && (
                      <p className="text-red-500 text-sm mt-1">{errors.assigned_countries}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm
                               placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500
                               focus:border-blue-500 sm:text-sm ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                      rows={4}
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Brief description of your company..."
                      disabled={!isEditing}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      {formData.description.length}/1000 characters
                    </p>
                  </div>

                  {isEditing && (
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="submit"
                        disabled={createCompanyMutation.isPending || updateCompanyMutation.isPending}
                      >
                        {createCompanyMutation.isPending || updateCompanyMutation.isPending
                          ? 'Saving...'
                          : id
                          ? 'Update Profile'
                          : 'Create Profile'}
                      </Button>
                    </div>
                  )}
                </form>
                )}

                {activeTab === 'members' && id && (
                  <TeamMembersTab
                    companyId={parseInt(id)}
                    companyName={currentCompany?.name || 'Company'}
                    isAdmin={
                      user?.is_system_user === true || 
                      (user && companies?.some((c: any) => c.id === parseInt(id) && (c.role === 'COMPANY_ADMIN' || c.system_role === 'ADMIN'))) ||
                      (user && !user.is_system_user) // Temporary fallback: allow all non-system users to manage team members
                    }
                  />
                )}

                {currentCompany && !isEditing && activeTab === 'details' && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                      <div>
                        <label className="font-medium text-gray-500">Created</label>
                        <p className="text-gray-900">
                          {new Date(currentCompany.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-500">Owner</label>
                        <p className="text-gray-900">
                          {user?.full_name || user?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {!id && (
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <i data-feather="info" className="w-5 h-5 text-blue-600 mt-0.5"></i>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        Why create a company profile?
                      </h3>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Organize all emission data under your organization</li>
                        <li>• Enable proper reporting and compliance tracking</li>
                        <li>• Set industry-specific benchmarks and targets</li>
                        <li>• Generate comprehensive emission reports</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}